import { CheckOutlined, EditOutlined, PrinterOutlined } from '@ant-design/icons';
import { css, cx } from '@emotion/css';
import { tabularNums } from '@medcom/shared-ui/styles/globalCss.ts';
import { Button, Popover, Tag, Tooltip, Typography, theme } from 'antd';
import { type FC, useState } from 'react';
import { TestTubeColor } from '../../../components/TestTubeColor.tsx';
import { LocalizationFieldType } from '../../../graphql/generated/graphql.ts';
import type { Localization, SelectedParameter } from '../../../hooks/store/useAppStore.ts';
import { fieldsTableHead } from '../../../styles/fieldsTable.ts';
import { testTubeColorProperties } from '../../../utils/enumHelpers.ts';
import { germanDecimal } from '../../../utils/localizationValue.ts';
import { NOT_AVAILABLE } from '../../../utils/placeholders.ts';
import { LocalizationsChooserModal } from '../selection/LocalizationsChooserModal.tsx';
import type { GeneratedLabel } from './generateLabels.ts';

type Specimen = SelectedParameter['specimens'][number];

/** Past this many analyses the chips collapse into a count; twenty on one Probe is ordinary. */
const INLINE_PARAMS = 2;

const PRINTER_HINT = 'Wird über den Spezial-Etikettendrucker gedruckt.';
const CAPACITY_HINT =
  'Das Fassungsvermögen des Röhrchens reicht für die gewählten Analysen nicht, daher wird mehr als ein Etikett erzeugt.';

/** One tube of a Material and the Etiketten it produces. A Material may have several, each with its
 *  own colour, print text and printer - so they cannot be folded into the Material's own line. */
type Tube = {
  id: string;
  name: string;
  color: string;
  labelText: string;
  specialPrinterRequired: boolean;
  count: number;
  /** A second Etikett of the same tube, which only ceil(volume / capacity) produces. */
  repeated: boolean;
};

/** One row per Lokalisation of the Material, with what was recorded for it. */
type LocationRow = {
  location: string;
  /** The analyses this Lokalisation was recorded for - not every one of them needs every site. */
  params: SelectedParameter[];
  /** keyed by field label; a field left empty is absent */
  values: Record<string, string>;
};

type Material = {
  spec: Specimen;
  params: SelectedParameter[];
  tubes: Tube[];
  locations: LocationRow[];
};

type MaterialBase = Omit<Material, 'locations'>;

/** No useMemo: emotion caches by hash, so a repeat call costs less than the dependency array. */
const useSpecimensClassNames = (flipParamNames: boolean) => {
  const { token } = theme.useToken();

  const headingStyle = css`
    display: flex;
    align-items: center;
    gap: ${token.marginXS}px;
    margin-bottom: ${token.marginXS}px;
  `;

  /** Below the blocks and to the left, where the Parameter list puts its own total. Summed from
   *  what the blocks show rather than from the label list, so the two cannot disagree. */
  const totalStyle = css`
    display: block;
    padding-top: ${token.paddingXS}px;
    font-size: ${token.fontSizeSM}px;
  `;

  /** No `grid-auto-flow: dense` - it would fill the hole a wide block leaves by pulling a later
   *  Material forward. The wide ones are sorted to the front instead. */
  const listStyle = css`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    /* stretch, so two blocks beside each other end at the same edge even when one carries a
       Lokalisationszeile and the other does not */
    align-items: stretch;
    gap: ${token.marginXS}px;

    @media (min-width: ${token.screenLG}px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  `;

  const wideStyle = css`
    grid-column: 1 / -1;
  `;

  /** Same corner the bordered Descriptions below uses, so the two sections read as one page. */
  const materialStyle = css`
    border: 1px solid ${token.colorBorderSecondary};
    border-radius: ${token.borderRadiusLG}px;
    padding: ${token.paddingXS}px ${token.paddingSM}px;
    overflow-x: auto;
  `;

  /** Baseline, not centre - the row mixes three text sizes, and centring their boxes leaves the
   *  glyphs at different heights. Icons have no baseline, so those stay centred. */
  const headStyle = css`
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: ${token.marginXS}px;

    .anticon {
      align-self: center;
    }
  `;

  const swatchStyle = css`
    align-self: center;
    display: inline-flex;
  `;

  /** The weight a name gets in a list here; 600 is the section heading's, see param-name-display. */
  const nameStyle = css`
    font-weight: 500;
  `;

  const tubeStyle = css`
    font-size: ${token.fontSizeSM}px;
    color: ${token.colorTextTertiary};
  `;

  const forLabelStyle = css`
    font-size: ${token.fontSizeSM}px;
    color: ${token.colorTextTertiary};
    margin-right: -${token.marginXXS}px;
  `;

  const paramPopoverStyle = css`
    display: flex;
    flex-wrap: wrap;
    gap: ${token.marginXXS}px;
    max-width: 420px;
  `;

  /** The shape both wear; the face is decided per content, not per position. */
  const chipStyle = css`
    border-radius: 999px;
    padding-inline: ${token.paddingXS}px;
    margin-inline-end: 0;
  `;

  /** Mono is the Kurzname's, because that one is a code - a Langbezeichnung is not, and neither is
   *  the count that replaces the list. */
  const analysisTagStyle = css`
    ${chipStyle};
    font-family: ${flipParamNames ? 'inherit' : token.fontFamilyCode};
  `;

  /** A button, because the analyses are only named behind it - a Tag renders a span that no
   *  keyboard reaches. Shaped like the chips it stands in for. */
  const paramCountStyle = css`
    ${chipStyle};
    height: auto;
    border: 1px solid ${token.colorBorder};
    font-size: ${token.fontSizeSM}px;
    line-height: ${token.fontSizeSM + 8}px;
  `;

  /** The trailing group instead of an empty spacer: a contentless flex item has no baseline and
   *  would push the row it is meant to leave alone. */
  const trailingStyle = css`
    display: inline-flex;
    align-self: center;
    align-items: center;
    gap: ${token.marginXS}px;
    margin-inline-start: auto;
  `;

  const countStyle = css`
    font-size: ${token.fontSizeSM}px;
    color: ${token.colorTextSecondary};
    white-space: nowrap;
  `;

  /** Only the branch without Lokalisationen repeats a tube, and only over its capacity. */
  const countWarnStyle = css`
    color: ${token.colorWarningText};
    background: ${token.colorWarningBg};
    border: 1px solid ${token.colorWarningBorder};
    border-radius: ${token.borderRadiusXS}px;
    padding-inline: ${token.paddingXXS}px;
  `;

  const printerStyle = css`
    color: ${token.colorTextTertiary};
  `;

  /** Only where a Material has more than one tube - otherwise the head carries it. */
  const tubeLineStyle = css`
    display: flex;
    align-items: center;
    gap: ${token.marginXS}px;
    margin-top: ${token.marginXXS}px;
    font-size: ${token.fontSizeSM}px;
  `;

  const tableStyle = css`
    /* antd styles every table inside its wrapper, and this one may sit in a Modal's - keep it ours */
    && {
      width: auto;
      table-layout: auto;
      border-collapse: collapse;
    }

    margin-top: ${token.marginSM}px;
    font-size: ${token.fontSize}px;


    td {
      padding: ${token.paddingXS}px ${token.marginLG}px 0 0;
      vertical-align: baseline;
    }

    th:last-child,
    td:last-child {
      padding-right: 0;
    }
  `;

  const valueStyle = css`
    font-weight: 500;
  `;

  /** The chips of one row stay together rather than wrapping into the next field's column. */
  const rowParamsStyle = css`
    white-space: nowrap;
  `;

  /** Without extra fields the table would be a header over a single column - a line does it. */
  const locationLineStyle = css`
    margin-top: ${token.marginXXS}px;
  `;

  const locationLabelStyle = css`
    font-size: ${token.fontSizeSM}px;
    color: ${token.colorTextTertiary};
    margin-right: ${token.marginXS}px;
  `;

  const emptyStyle = css`
    color: ${token.colorTextTertiary};
  `;

  const noteStyle = css`
    display: block;
    margin-top: ${token.marginXS}px;
    font-size: ${token.fontSizeSM}px;
  `;

  return {
    headingStyle,
    totalStyle,
    listStyle,
    wideStyle,
    materialStyle,
    headStyle,
    swatchStyle,
    nameStyle,
    tubeStyle,
    forLabelStyle,
    paramPopoverStyle,
    analysisTagStyle,
    paramCountStyle,
    trailingStyle,
    countStyle,
    countWarnStyle,
    printerStyle,
    tubeLineStyle,
    tableStyle,
    tableHeadStyle: fieldsTableHead(token),
    valueStyle,
    rowParamsStyle,
    locationLineStyle,
    locationLabelStyle,
    emptyStyle,
    noteStyle,
  };
};

/**
 * The Anforderung's Proben: one block per Material with its Röhrchen, Lokalisationen and the
 * Etiketten that follow. Keyed by Material because that is what an Etikett hangs off.
 *
 * Kasse, price and Verrechnung stay in the Parameter list above - they belong to the analysis.
 */
export const Specimens: FC<{
  selectedParams: SelectedParameter[];
  localizations: Localization[];
  labels: GeneratedLabel[];
  requestIdForReorder: string;
  flipParamNames: boolean;
}> = ({ selectedParams, localizations, labels, requestIdForReorder, flipParamNames }) => {
  const [chooserOpen, setChooserOpen] = useState(false);
  const styles = useSpecimensClassNames(flipParamNames);

  // Every Material of the Auftrag, in the order the parameters were picked. A Material shared by two
  // analyses appears once and collects both - that is the whole point of keying by Material.
  const materials: Material[] = selectedParams
    .flatMap(param => param.specimens.map(spec => ({ param, spec })))
    .reduce<MaterialBase[]>((acc, { param, spec }) => {
      const known = acc.find(it => it.spec.id === spec.id);
      if (known) {
        known.params.push(param);
        return acc;
      }

      acc.push({
        spec,
        params: [param],
        tubes: labels
          .filter(it => it.specimenId === spec.id)
          .reduce<Tube[]>((tubes, label) => {
            const tube = tubes.find(it => it.id === label.testTubeId);
            if (tube) {
              tube.count += 1;
              tube.repeated = tube.repeated || label.repetition > 0;
              return tubes;
            }
            tubes.push({
              id: label.testTubeId,
              name: label.testTubeName,
              color: label.testTubeColor,
              labelText: label.testTubeLabelText,
              specialPrinterRequired: label.specialPrinterRequired,
              count: 1,
              repeated: label.repetition > 0,
            });
            return tubes;
          }, []),
      });
      return acc;
    }, [])
    // A second pass, because the analyses of a Material are only complete once every selected
    // parameter has been seen - and a Lokalisation is only ours if one of them recorded it.
    .map(material => ({
      ...material,
      // rendered in stored order - that is the configured anatomical order
      locations: localizations
        .filter(loc => loc.specimenId === material.spec.id)
        .reduce<LocationRow[]>((rows, loc) => {
          // Deselecting a parameter leaves its Lokalisationen in the store on purpose, so they come
          // back when it is picked again. They produce no Etikett - OrderLabelGenerator filters by
          // the paramIds of the order - so listing them here would contradict the count beside them.
          const param = material.params.find(it => it.id === loc.paramId);
          if (!param) {
            return rows;
          }
          const known = rows.find(it => it.location === loc.location);
          if (known) {
            known.params.push(param);
            return rows;
          }
          rows.push({
            location: loc.location,
            params: [param],
            values: Object.fromEntries(loc.fieldValues.map(v => [v.label, v.value])),
          });
          return rows;
        }, []),
    }));

  if (!materials.length) {
    return null;
  }

  /** Only a Lokalisationstabelle needs the full row; without extra fields it is a single line. */
  const isWide = (material: Material) => !!material.locations.length && !!material.spec.localizationFields.length;

  // stable, so within each half the Materialien keep the order their parameters were picked in
  const ordered = [...materials].sort((a, b) => Number(isWide(b)) - Number(isWide(a)));

  const totalLabels = materials.reduce((sum, material) => sum + material.tubes.reduce((tubes, tube) => tubes + tube.count, 0), 0);

  const hasLocalizations = materials.some(it => it.spec.requiresLocalizations);

  /** What the tube adds over the Material's own name, and over its own print text. */
  const tubeLabel = (tube: Tube, shownName: string) => {
    const suffix = tube.name === shownName ? '' : tube.name.startsWith(shownName) ? tube.name.slice(shownName.length).trim() : tube.name;
    // each string once - a tube named after its own print text would otherwise show it twice
    const shown = suffix || shownName;
    return [suffix, shown === tube.labelText ? '' : tube.labelText].filter(Boolean).join(' · ');
  };

  /** Zero happens when a Material's Lokalisationen were never chosen - a statement, not a sum. */
  const countLabel = (count: number) => (count === 0 ? 'Keine Etiketten' : `${count} ${count === 1 ? 'Etikett' : 'Etiketten'}`);

  return (
    <div>
      <h3 className={styles.headingStyle}>
        Proben
        {hasLocalizations && (
          <Button icon={<EditOutlined />} size="small" onClick={() => setChooserOpen(true)}>
            Lokalisationen bearbeiten
          </Button>
        )}
      </h3>

      <div className={styles.listStyle}>
        {ordered.map(material => {
          const fields = material.spec.localizationFields;
          const total = material.tubes.reduce((sum, it) => sum + it.count, 0);
          const overCapacity = material.tubes.some(it => it.repeated);
          const single = material.tubes.length === 1 ? material.tubes[0] : null;
          // Only where the rows actually differ: the chooser seeds a new Lokalisation with every
          // analysis of the Material, so "more than one analysis" is the default state and the
          // column would repeat the head's own chips on every row.
          const showRowParams = material.locations.some(row => row.params.length !== material.params.length);
          const showTable = !!fields.length || showRowParams;
          return (
            <div key={material.spec.id} className={`${styles.materialStyle} ${isWide(material) ? styles.wideStyle : ''}`}>
              <div className={styles.headStyle}>
                {single && (
                  <span className={styles.swatchStyle}>
                    <TestTubeColor properties={testTubeColorProperties(single.color)} withText={false} />
                  </span>
                )}
                <span className={styles.nameStyle}>{material.spec.name}</span>
                {single && !!tubeLabel(single, material.spec.name) && (
                  <span className={styles.tubeStyle}>{tubeLabel(single, material.spec.name)}</span>
                )}

                <span className={styles.forLabelStyle}>für</span>
                {material.params.length > INLINE_PARAMS ? (
                  <Popover
                    trigger="click"
                    content={
                      <div className={styles.paramPopoverStyle}>
                        {material.params.map(param => (
                          <Tag key={param.id} className={styles.analysisTagStyle}>
                            {flipParamNames ? param.longName : param.shortName}
                          </Tag>
                        ))}
                      </div>
                    }
                  >
                    <Button type="link" size="small" className={styles.paramCountStyle}>
                      {material.params.length} Parameter
                    </Button>
                  </Popover>
                ) : (
                  material.params.map(param => (
                    <Tag key={param.id} className={styles.analysisTagStyle}>
                      {flipParamNames ? param.longName : param.shortName}
                    </Tag>
                  ))
                )}

                {(!requestIdForReorder || single?.specialPrinterRequired) && (
                  <span className={styles.trailingStyle}>
                    {!requestIdForReorder &&
                      (overCapacity ? (
                        <Tooltip title={CAPACITY_HINT}>
                          <span className={`${styles.countStyle} ${styles.countWarnStyle}`}>{countLabel(total)}</span>
                        </Tooltip>
                      ) : (
                        <span className={styles.countStyle}>{countLabel(total)}</span>
                      ))}
                    {single?.specialPrinterRequired && (
                      <Tooltip title={PRINTER_HINT}>
                        <PrinterOutlined className={styles.printerStyle} />
                      </Tooltip>
                    )}
                  </span>
                )}
              </div>

              {/* several tubes carry their own colour, print text and printer - one line each */}
              {!single &&
                material.tubes.map(tube => (
                  <div key={tube.id} className={styles.tubeLineStyle}>
                    <TestTubeColor properties={testTubeColorProperties(tube.color)} withText={false} />
                    <span>{tube.name}</span>
                    {!!tubeLabel(tube, tube.name) && <span className={styles.tubeStyle}>{tubeLabel(tube, tube.name)}</span>}
                    <span className={styles.trailingStyle}>
                      {!requestIdForReorder && tube.repeated && (
                        <Tooltip title={CAPACITY_HINT}>
                          <span className={`${styles.countStyle} ${styles.countWarnStyle}`}>{countLabel(tube.count)}</span>
                        </Tooltip>
                      )}
                      {tube.specialPrinterRequired && (
                        <Tooltip title={PRINTER_HINT}>
                          <PrinterOutlined className={styles.printerStyle} />
                        </Tooltip>
                      )}
                    </span>
                  </div>
                ))}

              {!!material.locations.length && !showTable && (
                <div className={styles.locationLineStyle}>
                  <span className={styles.locationLabelStyle}>{material.locations.length === 1 ? 'Lokalisation' : 'Lokalisationen'}</span>
                  {material.locations.map(it => it.location).join(' · ')}
                </div>
              )}

              {!!material.locations.length && showTable && (
                <table className={cx(styles.tableStyle, styles.tableHeadStyle)}>
                  <thead>
                    <tr>
                      <th>Lokalisation</th>
                      {showRowParams && <th>für</th>}
                      {fields.map(field => (
                        <th key={field.label}>{field.unit ? `${field.label} (${field.unit})` : field.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {material.locations.map(row => (
                      <tr key={row.location}>
                        <td>{row.location}</td>
                        {showRowParams && (
                          <td className={styles.rowParamsStyle}>
                            {row.params.map(param => (
                              <Tag key={param.id} className={styles.analysisTagStyle}>
                                {flipParamNames ? param.longName : param.shortName}
                              </Tag>
                            ))}
                          </td>
                        )}
                        {fields.map(field => (
                          <td
                            key={field.label}
                            className={`${styles.valueStyle} ${field.inputType === LocalizationFieldType.NUMBER ? tabularNums : ''}`}
                          >
                            {row.values[field.label] ? (
                              germanDecimal(row.values[field.label], field.inputType)
                            ) : (
                              <span className={styles.emptyStyle}>{NOT_AVAILABLE}</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>

      {!requestIdForReorder && (
        <Typography.Text type="secondary" className={styles.totalStyle}>
          {totalLabels === 1 ? '1 Etikett insgesamt' : `${totalLabels} Etiketten insgesamt`}
        </Typography.Text>
      )}

      {requestIdForReorder && (
        <Typography.Text type="secondary" className={styles.noteStyle}>
          Keine neuen Etiketten für Nachforderung benötigt – die Probe liegt bereits im Labor.
        </Typography.Text>
      )}
      {!requestIdForReorder && materials.some(it => it.tubes.some(tube => tube.repeated)) && (
        <Typography.Text type="secondary" className={styles.noteStyle}>
          Eine hervorgehobene Anzahl bedeutet, dass das Fassungsvermögen des Röhrchens für die gewählten Analysen nicht reicht und deshalb
          zusätzliche Etiketten erzeugt werden.
        </Typography.Text>
      )}

      {hasLocalizations && (
        <LocalizationsChooserModal
          selectedParams={selectedParams}
          open={chooserOpen}
          requestIdForReorder={requestIdForReorder}
          okText="OK"
          okIcon={<CheckOutlined />}
          closable={false}
          flipParamNames={flipParamNames}
          onClose={() => setChooserOpen(false)}
          onSuccess={() => setChooserOpen(false)}
        />
      )}
    </div>
  );
};
